import { mergeStyles, Text, useTheme } from '@fluentui/react';
import { RootState } from 'app/store';
import { IGCData } from 'global/Reducer/dataSlice';
import Formatters from "global/Utility/Formatters";
import { formatDate, parseDate } from 'global/Utility/TSFunctions';
import { bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import { Ibcbs_groupconfigurationsnapshot_extended } from 'model/CRM.type';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import LinkTo from './controls/LinkTo';

interface IProps {
    data: IGCData,
    path?: string,
}

const SnapshotSummary: React.FunctionComponent<IProps> = (props) => {
    const snapshot = props.data.snapshot as Ibcbs_groupconfigurationsnapshot_extended;
    const json = props.data.json;

    const theme = useTheme();

    const state = useSelector((state: RootState) => state);

    const headerStyles = { root: { fontWeight: '680' } };

    const tBodyClass = useMemo(() => {
        return mergeStyles({
            "td": {
            },
            "& > :nth-child(even)": {
                backgroundColor: theme.semanticColors.bodyDivider,
            }
        });
    }, [theme]);

    const styleSummary = useMemo(() => {
        const styleSummary: Record<string, React.CSSProperties> = {
            "dl": {
                width: '100%',
                padding: '5px',
            },
            "dt": {
                fontWeight: 680,
                textAlign: 'right',
                width: "210px",
                padding: '5px',
            },
            "dd": {
                padding: '5px',
            }
        }
        return styleSummary;
    }, []);

    return (
        <div style={{ flexBasis: '400px', flexGrow: 1 }}>
            <div style={{ alignSelf: 'end', margin: '0.5em', borderBottom: '1px solid gray' }}>
                <Text>Snapshot Information</Text>
            </div>

            <table style={styleSummary.dl}>
                <tbody className={tBodyClass}>
                    <tr>
                        <td style={styleSummary.dt}><Text styles={headerStyles}>Name</Text></td>
                        <td style={styleSummary.dd}><LinkTo snapshot={snapshot} /></td>
                    </tr>

                    {state.status.debug.diagnosis && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>⚙️ Snapshot</Text></td>
                                <td style={styleSummary.dd}><Text>{snapshot.bcbs_filename || snapshot.bcbs_snapshot_name}</Text></td>
                            </tr>
                        </>
                    )}

                    {state.status.debug.diagnosis && snapshot._bcbs_releaseid_value && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>⚙️ Release</Text></td>
                                <td style={styleSummary.dd}>
                                    <LinkTo
                                        bcbs_config_release={{
                                            bcbs_name: snapshot['_bcbs_releaseid_value@OData.Community.Display.V1.FormattedValue'],
                                            bcbs_config_releaseid: snapshot._bcbs_releaseid_value!,
                                        }}
                                    />
                                </td>
                            </tr>
                        </>
                    )}


                    {state.status.debug.diagnosis && snapshot.bcbs_operativedate && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>⚙️ Operative Date</Text></td>
                                <td style={styleSummary.dd}><Text>{Formatters.ToShortDateTimeFromJSDate(parseDate(snapshot.bcbs_operativedate, 'yyyy-MM-ddThh:mm:ssZ as local'))}</Text></td>
                            </tr>
                        </>
                    )}

                    {!!snapshot.statuscode && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Status</Text></td>
                                <td style={styleSummary.dd}><Text>{bcbs_groupconfigurationsnapshot.statuscode[snapshot.statuscode as bcbs_groupconfigurationsnapshot.Tstatuscode].name ?? 'N/A'}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot.bcbs_effectivedate && snapshot.bcbs_effectivedate && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Effective Date</Text></td>
                                <td style={styleSummary.dd}><Text>{formatDate(parseDate(snapshot.bcbs_effectivedate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot.createdon && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Created On</Text></td>
                                <td style={styleSummary.dd}><Text>{Formatters.ToShortDateTime(snapshot.createdon)}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot._createdby_value && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Created By</Text></td>
                                <td style={styleSummary.dd}><Text>{snapshot['_createdby_value@OData.Community.Display.V1.FormattedValue']}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot.modifiedon && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Modified On</Text></td>
                                <td style={styleSummary.dd}><Text>{Formatters.ToShortDateTime(snapshot.modifiedon)}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot.bcbs_changetypecode && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Change Type</Text></td>
                                <td style={styleSummary.dd}><Text>{snapshot['bcbs_changetypecode@OData.Community.Display.V1.FormattedValue']}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot.bcbs_fundingtypecode && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Funding Type</Text></td>
                                <td style={styleSummary.dd}><Text>{snapshot['bcbs_fundingtypecode@OData.Community.Display.V1.FormattedValue']}</Text></td>
                            </tr>
                        </>
                    )}

                    {json?.dualFunded && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Dual Funded</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.dualFunded.value ? "Yes" : "No"}</Text></td>
                            </tr>
                        </>
                    )}

                    {json?.erisa && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>ERISA</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.erisa.name}</Text></td>
                            </tr>
                        </>
                    )}

                    {snapshot._bcbs_originatingsnapshotid_value && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Originating Snapshot</Text></td>
                                <td style={styleSummary.dd}>
                                    <LinkTo
                                        snapshot={{
                                            bcbs_name: snapshot['_bcbs_originatingsnapshotid_value@OData.Community.Display.V1.FormattedValue'],
                                            bcbs_groupconfigurationsnapshotid: snapshot._bcbs_originatingsnapshotid_value!,
                                            _bcbs_groupid_value: snapshot._bcbs_groupid_value
                                        }}
                                    />
                                </td>
                            </tr>
                        </>
                    )}



                    {snapshot._bcbs_originatingsnapshot2id_value && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Originating Snapshot</Text></td>
                                <td style={styleSummary.dd}>
                                    <LinkTo
                                        snapshot={{
                                            bcbs_name: snapshot['_bcbs_originatingsnapshot2id_value@OData.Community.Display.V1.FormattedValue'],
                                            bcbs_groupconfigurationsnapshotid: snapshot._bcbs_originatingsnapshot2id_value!,
                                            _bcbs_groupid_value: snapshot._bcbs_groupid_value
                                        }}
                                    />
                                </td>
                            </tr>
                        </>
                    )}

                    {((props.path?.indexOf('comparison') && props.path?.indexOf('comparison') !== -1) || (props.path?.indexOf('underwritingReview') && props.path?.indexOf('underwritingReview') !== -1) || (props.path?.indexOf('merge') && props.path?.indexOf('merge') !== -1)) && (
                        <>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Grandfathered</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.grandFathered?.name ?? 'No Value'}</Text></td>
                            </tr>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Line of Business</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.lineOfBusiness?.name ?? 'No Value'}</Text></td>
                            </tr>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>MNIL</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.MNIL?.value ? 'Yes' : 'No'}</Text></td>
                            </tr>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Reconciliation</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.reconciliation?.value ? 'Yes' : 'No'}</Text></td>
                            </tr>
                            <tr>
                                <td style={styleSummary.dt}><Text styles={headerStyles}>Opportunity</Text></td>
                                <td style={styleSummary.dd}><Text>{json?.opportunity?.name ?? 'No Value'}</Text></td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default SnapshotSummary;